import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import Header from '../components/layout/Header';
import TabNavigation, { DETAIL_VIEW_TABS } from '../components/layout/TabNavigation';
import { getSectionStatus } from '../utils/sectionStatus';
import { getEnabledFeatureIds } from '../config/featurePresets';
import GuideAssistant from '../components/onboarding/GuideAssistant';
import AIAssistant from '../components/ai/AIAssistant';
import SpoonMascot from '../components/ai/SpoonMascot';
import { getAIPlaceholderForStep, getAIContextForStep } from '../config/aiStepContext';
import { ChevronRight, LayoutDashboard, List, Search, Sparkles, X } from 'lucide-react';
import FeatureSearch from '../components/search/FeatureSearch';
import DashboardOverview from '../components/dashboard/DashboardOverview';
import OperationsPlan from '../components/business-plan/OperationsPlan';
import FinancialProjections from '../components/financial/FinancialProjections';
import VendorsAndExpensesView from '../components/vendors/VendorsAndExpensesView';
import TimelineManager from '../components/project/TimelineManager';
import BusinessAnalytics from '../components/analytics/BusinessAnalytics';
import BrandingPlanner from '../components/branding/BrandingPlanner';
import StartupAndOpeningPlan from '../components/startup/StartupAndOpeningPlan';
import ConceptAndPitch from '../components/ideation/ConceptAndPitch';
import MarketAndCompetition from '../components/business-plan/MarketAndCompetition';
import OfferAndMarketing from '../components/business-plan/OfferAndMarketing';
import TeamAndCapTable from '../components/business-plan/TeamAndCapTable';
import EquipmentAndMenu from '../components/equipment/EquipmentAndMenu';
import ComplianceHub from '../components/compliance/ComplianceHub';
import PricingPage from '../components/payment/PricingPage';
import SubscriptionManager from '../components/payment/SubscriptionManager';
import AdminDashboard from '../components/admin/AdminDashboard';
import DocumentImporter from '../components/import/DocumentImporter';
import WelcomeTour from '../components/onboarding/WelcomeTour';
import TrialExpirationBanner from '../components/trial/TrialExpirationBanner';
import OnboardingDocumentsWorkflow from '../components/onboarding/OnboardingDocumentsWorkflow';
import ReportsView from '../components/reports/ReportsView';
import SOPManager from '../components/sops/SOPManager';
import ProcessMapView from '../components/process/ProcessMapView';
import ProjectSetupModal from '../components/layout/ProjectSetupModal';

const Dashboard = () => {
  const { state, actions } = useApp();
  const [showOverview, setShowOverview] = useState(true);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [showGuideAssistant, setShowGuideAssistant] = useState(false);
  const [showAIPopup, setShowAIPopup] = useState(false);
  const [showFeatureSearch, setShowFeatureSearch] = useState(false);

  const currentDraft = state.drafts?.find(d => d.id === state.currentDraftId);
  const enabledFeatureIds = useMemo(
    () => getEnabledFeatureIds(currentDraft?.enabledFeatures),
    [currentDraft?.enabledFeatures]
  );

  // Keep activeTab within this project's enabled features
  useEffect(() => {
    if (!enabledFeatureIds || enabledFeatureIds.length === 0) return;
    if (enabledFeatureIds.includes(state.activeTab)) return;
    actions.setActiveTab(enabledFeatureIds[0]);
  }, [enabledFeatureIds, state.activeTab, actions]);

  const sectionStatus = useMemo(() => {
    const base = getSectionStatus(currentDraft, state.openingPlanProgress?.completedTaskIds || []);
    if (!enabledFeatureIds) return base;
    return Object.fromEntries(
      Object.entries(base).filter(([id]) => enabledFeatureIds.includes(id))
    );
  }, [currentDraft, state.openingPlanProgress?.completedTaskIds, enabledFeatureIds]);
  const completedCount = useMemo(() => Object.values(sectionStatus).filter(s => s.completed).length, [sectionStatus]);
  const totalSections = useMemo(() => Object.keys(sectionStatus).length, [sectionStatus]);
  const activeTabInfo = DETAIL_VIEW_TABS.find(t => t.id === state.activeTab);
  const currentSectionLabel = activeTabInfo?.label ?? 'Detailed View';
  const isCurrentSectionComplete = sectionStatus[state.activeTab]?.completed;

  const renderActiveTab = () => {
    switch (state.activeTab) {
      case 'startup-and-opening':
        return <StartupAndOpeningPlan />;
      case 'process-map':
        return <ProcessMapView />;
      case 'concept-pitch':
        return <ConceptAndPitch />;
      case 'market-competition':
        return <MarketAndCompetition />;
      case 'offer-marketing':
        return <OfferAndMarketing />;
      case 'timeline':
        return <TimelineManager />;
      case 'operations':
        return <OperationsPlan />;
      case 'team-cap-table':
        return <TeamAndCapTable />;
      case 'financials':
        return <FinancialProjections />;
      case 'vendors-expenses':
      case 'vendors':
      case 'ledger':
        return <VendorsAndExpensesView />;
      case 'equipment-menu':
        return <EquipmentAndMenu />;
      case 'branding':
        return <BrandingPlanner />;
      case 'compliance':
        return <ComplianceHub />;
      case 'documents':
        return (
          <div className="space-y-6">
            <OnboardingDocumentsWorkflow />
          </div>
        );
      case 'reports':
        return <ReportsView />;
      case 'sops':
        return <SOPManager />;
      case 'business-analytics':
        return <BusinessAnalytics />;
      case 'pricing':
        return <PricingPage />;
      case 'subscription':
        return <SubscriptionManager />;
      case 'admin':
        return <AdminDashboard />;
      case 'import':
        return <DocumentImporter />;
      default:
        return <StartupAndOpeningPlan />;
    }
  };

  // Check if user should see welcome tour (only once for new users)
  React.useEffect(() => {
    if (!state.isAuthenticated || !state.userId) return;
    
    const tourCompleted = localStorage.getItem('welcomeTourCompleted');
    const isNewUser = !tourCompleted;
    
    if (isNewUser) {
      // Show tour after a short delay to ensure dashboard is loaded
      const timer = setTimeout(() => {
        setShowWelcomeTour(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [state.isAuthenticated, state.userId]);

  const handleTourComplete = () => {
    localStorage.setItem('welcomeTourCompleted', 'true');
    setShowWelcomeTour(false);
  };

  const handleUpgrade = () => {
    actions.setActiveTab('pricing');
  };

  const handleSwitchToDetailed = (tabId) => {
    setShowOverview(false);
    if (tabId) actions.setActiveTab(tabId);
  };

  // Cmd/Ctrl+K to open feature search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowFeatureSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // First or no project: show project setup (opening new / buying / helping existing) before dashboard
  const hasNoDrafts = state.isAuthenticated && (!state.drafts || state.drafts.length === 0);
  if (hasNoDrafts) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <ProjectSetupModal isOpen={true} isFirstProject={true} allowClose={false} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* View mode toggle: Dashboard vs Open Restaurant */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-[95%] xl:max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setShowFeatureSearch(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 text-sm transition-colors"
              title="Search features (Ctrl+K)"
            >
              <Search className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">Search features...</span>
              <kbd className="hidden md:inline text-xs text-slate-400 ml-1">⌘K</kbd>
            </button>
            <span className="text-sm text-slate-500 hidden md:inline">
              {showOverview ? 'Dashboard' : 'Open Restaurant'}
            </span>
            <div className="inline-flex p-1 bg-slate-100 rounded-lg" role="group" aria-label="Switch between dashboard and open restaurant view">
              <button
                onClick={() => setShowOverview(true)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  showOverview ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </button>
              <button
                onClick={() => setShowOverview(false)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  !showOverview ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <List className="w-4 h-4" />
                Open Restaurant
              </button>
            </div>
          </div>
        </div>
      </div>
      <TrialExpirationBanner onUpgrade={handleUpgrade} />
      <div className="max-w-[95%] xl:max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-8">
        {showOverview ? (
          <DashboardOverview onSwitchToDetailed={handleSwitchToDetailed} />
        ) : (
          <div className="modern-card overflow-hidden shadow-2xl flex flex-col">
            {/* Progress bar at top: breadcrumb + section title */}
            <div className="px-6 lg:px-8 pt-4 pb-2 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
              <nav className="flex items-center gap-2 text-sm text-gray-500 mb-2" aria-label="Breadcrumb">
                <button
                  type="button"
                  onClick={() => setShowOverview(true)}
                  className="hover:text-blue-600 font-medium transition-colors"
                >
                  Dashboard
                </button>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700 font-medium">Open Restaurant</span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900 font-semibold" aria-current="page">{currentSectionLabel}</span>
              </nav>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{currentSectionLabel}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {totalSections > 0 ? (
                  <>
                    <span className="font-medium text-gray-700">{completedCount} of {totalSections}</span> plan sections complete
                    {!isCurrentSectionComplete && (
                      <span className="ml-2 text-amber-600 font-medium">• Complete this section below</span>
                    )}
                  </>
                ) : null}
              </p>
            </div>
            {/* Main content (left) + tabs (right) */}
            <div className="flex flex-1 min-h-0">
              <div className="flex-1 min-w-0 flex flex-col min-h-0">
                <div className="flex-1 min-h-0 p-6 lg:p-8 bg-gradient-to-br from-white to-gray-50 overflow-auto">
                  {renderActiveTab()}
                </div>
                {/* Step-aware AI Assistant: available on every step */}
                <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4">
                  <AIAssistant
                    section={state.activeTab}
                    context={getAIContextForStep(state.activeTab, state)}
                    placeholder={getAIPlaceholderForStep(state.activeTab)}
                    showQuickActions={false}
                  />
                </div>
              </div>
              <TabNavigation sectionStatus={sectionStatus} variant="sidebar" />
            </div>
          </div>
        )}
      </div>
      <FeatureSearch
        isOpen={showFeatureSearch}
        onClose={() => setShowFeatureSearch(false)}
        onSelect={(tabId) => handleSwitchToDetailed(tabId)}
      />

      <WelcomeTour
        isOpen={showWelcomeTour}
        onComplete={handleTourComplete}
        onSkip={handleTourComplete}
      />

      {/* AI Assistant - spoon mascot FAB (pops up to help) */}
      <button
        type="button"
        onClick={() => setShowAIPopup(true)}
        className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-white border-2 border-amber-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 flex items-center justify-center transition-all hover:scale-110 active:scale-95 overflow-hidden"
        aria-label="Open AI assistant"
        title="Ask the spoon — AI help"
      >
        <SpoonMascot size={48} animated />
      </button>
      {/* Step-by-step guide FAB - left of spoon */}
      <button
        type="button"
        onClick={() => setShowGuideAssistant(true)}
        className="fixed bottom-6 right-24 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        aria-label="Open step-by-step guide"
      >
        <Sparkles className="w-7 h-7" />
      </button>
      <GuideAssistant
        isOpen={showGuideAssistant}
        onClose={() => setShowGuideAssistant(false)}
        sectionStatus={sectionStatus}
      />

      {/* AI Assistant popup - slide-up panel with spoon */}
      {showAIPopup && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/40" aria-hidden onClick={() => setShowAIPopup(false)} />
          <div className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-t-2xl shadow-2xl flex flex-col animate-slide-up">
            <div className="flex-shrink-0 flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50 rounded-t-2xl">
              <SpoonMascot size={48} animated />
              <div className="flex-1">
                <h2 className="text-lg font-bold text-gray-900">Spoon Assistant</h2>
                <p className="text-sm text-gray-600">Ask me anything about your restaurant plan.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAIPopup(false)}
                className="p-2 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-6">
              <AIAssistant
                section={showOverview ? null : state.activeTab}
                context={showOverview ? {} : getAIContextForStep(state.activeTab, state)}
                placeholder={showOverview ? 'Ask me anything about your restaurant business plan...' : getAIPlaceholderForStep(state.activeTab)}
                showQuickActions={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 